import React from "react";
import { useNavigate } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = React.useState(false);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/profile/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/profile/photos/${id}`);
    await refresh();
  };

  if (!user) return null;

  const canPay = (user.photo_ids?.length || 0) >= 2;

  return (
    <div className="min-h-screen bg-dc-gradient p-4 py-8">
      <div className="max-w-2xl mx-auto glass-strong rounded-3xl p-6 sm:p-8">
        <h1 className="font-display text-2xl font-extrabold">Complete your profile</h1>
        <p className="text-sm text-purple-200/70 mt-1">Upload at least 2 photos, pay ₹199 via UPI, then wait for admin approval.</p>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-purple-200/70">Step 1 · Photos ({user.photo_ids?.length || 0}/2 required)</div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(user.photo_ids || []).map((pid) => (
              <div key={pid} className="relative aspect-square rounded-2xl overflow-hidden border border-purple-500/30 bg-black/30">
                <img src={photoUrl(pid)} alt="" className="w-full h-full object-cover" />
                <button data-testid={`remove-photo-${pid}`} onClick={() => remove(pid)} className="absolute top-2 right-2 bg-black/70 rounded-full p-1.5 hover:bg-rose-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-2xl border-2 border-dashed border-purple-400/40 bg-purple-900/20 flex flex-col items-center justify-center cursor-pointer hover:bg-purple-900/40">
              <Upload className="w-6 h-6 text-purple-200" />
              <span className="text-xs mt-2 text-purple-200">{busy ? "Uploading…" : "Add photo"}</span>
              <input data-testid="upload-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            </label>
          </div>
        </div>

        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-widest text-purple-200/70">Step 2 · Pay ₹199 entry</div>
          <button data-testid="pay-199" disabled={!canPay} onClick={() => nav("/pay")} className="btn-cta mt-3 w-full py-3 rounded-2xl disabled:opacity-50">
            Continue to UPI payment
          </button>
          {!canPay && (
            <div className="text-xs text-amber-300 mt-2">Upload at least 2 photos to continue.</div>
          )}
        </div>

        <div className="mt-8 text-xs text-purple-200/70">
          Status: <span className="font-semibold text-white">{user.status}</span>
        </div>
      </div>
    </div>
  );
}
